import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
	MagnifyingGlassIcon,
	AdjustmentsHorizontalIcon,
	TableCellsIcon,
	CalendarDaysIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	Badge,
	Calendar,
	DataTable,
	FilterDropdown,
	StatusBadge,
	TextStack,
} from '../../components/nextgen';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getWorkOrders, getWorkOrdersCalendar } from '../../api';
import { getUserFullName } from '../../components/helpers/functions';

// Due date status helper - COMMENTED OUT
// const getDueDateStatus = (dueDate, status) => {
// 	if (!dueDate || ['completed', 'cancelled'].includes(status)) {
// 		return { status: 'normal', className: 'text-gray-500' };
// 	}
//
// 	const now = moment().startOf('day');
// 	const due = moment(dueDate).startOf('day');
// 	const daysUntilDue = due.diff(now, 'days');
//
// 	if (daysUntilDue < 0) {
// 		return { status: 'overdue', className: 'text-red-600 font-medium' };
// 	}
// 	return { status: 'normal', className: 'text-gray-500' };
// };

// Stat Card
const StatCard = ({ label, value, valueColor = 'text-gray-900' }) => (
	<div className="bg-white border border-gray-200 rounded-lg p-5">
		<p className="text-sm text-gray-500 mb-1">{label}</p>
		<p className={`text-3xl font-semibold ${valueColor}`}>{value}</p>
	</div>
);

const WorkOrders = () => {
	const navigate = useNavigate();
	const { id: workOrderIdFromUrl } = useParams();
	const [searchParams] = useSearchParams();
	const assetIdFromUrl = searchParams.get('assetId');
	const statusFromUrl = searchParams.get('status');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');

	// View toggle
	const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'
	const [calendarMonth, setCalendarMonth] = useState(moment().startOf('month'));

	// Filters
	const [search, setSearch] = useState('');
	// Active statuses (everything except completed/cancelled)
	const ACTIVE_STATUSES = [
		'draft',
		'pending',
		'scheduled',
		'in_progress',
		'on_hold',
	];
	// Initialize status filter based on URL parameter or default to active statuses
	const [statusFilter, setStatusFilter] = useState(
		statusFromUrl === 'all' ? [] : ACTIVE_STATUSES,
	);

	// Handle status filter change with "all_active" special case
	const handleStatusFilterChange = (newValue) => {
		if (newValue.includes('all_active')) {
			// If "All active" was just selected, set to all active statuses
			if (!statusFilter.includes('all_active')) {
				setStatusFilter(ACTIVE_STATUSES);
			} else {
				// Remove all_active if it was deselected
				setStatusFilter(newValue.filter((v) => v !== 'all_active'));
			}
		} else {
			setStatusFilter(newValue);
		}
	};
	const [categoryFilter, setCategoryFilter] = useState('all');

	// Mobile infinite scroll
	const MOBILE_PAGE_SIZE = 10;
	const [mobileItemsToShow, setMobileItemsToShow] = useState(MOBILE_PAGE_SIZE);
	const observerRef = useRef(null);

	// Reset mobile pagination when filters change
	useEffect(() => {
		setMobileItemsToShow(MOBILE_PAGE_SIZE);
	}, [search, statusFilter, categoryFilter]);

	// Callback ref for infinite scroll
	const loadMoreRef = useCallback((node) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
		}

		if (node) {
			observerRef.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						setMobileItemsToShow((prev) => prev + MOBILE_PAGE_SIZE);
					}
				},
				{ root: null, rootMargin: '100px', threshold: 0 },
			);
			observerRef.current.observe(node);
		}
	}, []);

	// Data
	const {
		value: workOrdersData,
		execute: refreshWorkOrders,
		status: loadingStatus,
	} = useAsync(getWorkOrders, {
		defaultPayload: { pageSize: null },
	});

	// Calendar data
	const {
		value: calendarData,
		execute: refreshCalendarData,
		status: calendarLoadingStatus,
	} = useAsync(getWorkOrdersCalendar, { immediate: false });

	useEffect(() => {
		refreshWorkOrders({ pageSize: null });
	}, []);

	// Fetch calendar data when switching to calendar view or changing month
	useEffect(() => {
		if (viewMode === 'calendar') {
			refreshCalendarData({
				startDate: calendarMonth.clone().startOf('month').format('YYYY-MM-DD'),
				endDate: calendarMonth.clone().endOf('month').format('YYYY-MM-DD'),
			});
		}
	}, [viewMode, calendarMonth]);

	// Get unique categories for filter
	const categories = useMemo(() => {
		const cats = workOrdersData?.rows
			?.map((wo) => wo.category)
			.filter(Boolean)
			.reduce((acc, cat) => {
				if (!acc.find((c) => c.id === cat.id)) {
					acc.push(cat);
				}
				return acc;
			}, []);
		return cats || [];
	}, [workOrdersData]);

	// Process work orders (search and filter)
	const processedWorkOrders = useMemo(() => {
		let result = workOrdersData?.rows || [];

		// Asset filter from URL (hidden filter)
		if (assetIdFromUrl) {
			result = result.filter((wo) => wo.assetId === assetIdFromUrl);
		}

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(wo) =>
					wo.workOrderCode?.toLowerCase().includes(q) ||
					wo.title?.toLowerCase().includes(q),
			);
		}

		// Status filter (multi-select: empty array means all)
		// Also show completed work orders with missing service reports when filtering for active statuses
		if (statusFilter.length > 0) {
			const isFilteringActive =
				statusFilter.some((s) =>
					['draft', 'pending', 'scheduled', 'in_progress', 'on_hold'].includes(
						s,
					),
				) && !statusFilter.includes('completed');

			result = result.filter((wo) => {
				// Always include if status matches filter
				if (statusFilter.includes(wo.status)) return true;

				// When filtering for active statuses, also include completed work orders
				// that are missing a required service report
				if (isFilteringActive && wo.status === 'completed') {
					const missingServiceReport =
						wo.serviceReportRequired && !wo.serviceReportId;
					return missingServiceReport;
				}

				return false;
			});
		}

		// Category filter
		if (categoryFilter !== 'all') {
			result = result.filter((wo) => wo.categoryId === categoryFilter);
		}

		// Sort by earliest date (start date or due date)
		result = [...result].sort((a, b) => {
			// Get earliest relevant date for each work order
			const getEarliestDate = (wo) => {
				const dates = [wo.scheduledStartDate, wo.dueDate].filter(Boolean);
				if (dates.length === 0) return null;
				return dates.reduce((earliest, date) =>
					moment(date).isBefore(moment(earliest)) ? date : earliest,
				);
			};

			const dateA = getEarliestDate(a);
			const dateB = getEarliestDate(b);

			// Work orders without dates go to the end
			if (!dateA && !dateB) return 0;
			if (!dateA) return 1;
			if (!dateB) return -1;

			return moment(dateA).diff(moment(dateB));
		});

		return result;
	}, [workOrdersData, search, statusFilter, categoryFilter, assetIdFromUrl]);

	const handleRowClick = (workOrder) => {
		navigate(`/work-orders/${workOrder.id}`);
		openWorkOrderOverview({
			id: workOrder.id,
			onClose: (path) => {
				navigate(path || '/work-orders');
				refreshWorkOrders({ pageSize: null });
			},
		});
	};

	// Open work order from URL if ID is present
	useEffect(() => {
		if (workOrderIdFromUrl) {
			openWorkOrderOverview({
				id: workOrderIdFromUrl,
				onClose: () => {
					navigate('/work-orders');
					refreshWorkOrders({ pageSize: null });
				},
			});
		}
	}, [workOrderIdFromUrl]);

	// Stats
	const total = workOrdersData?.rows?.length || 0;
	const pending =
		workOrdersData?.rows?.filter((wo) => wo.status === 'pending').length || 0;
	const scheduled =
		workOrdersData?.rows?.filter((wo) => wo.status === 'scheduled').length || 0;
	const inProgress =
		workOrdersData?.rows?.filter((wo) => wo.status === 'in_progress').length ||
		0;
	const completed =
		workOrdersData?.rows?.filter((wo) => wo.status === 'completed').length || 0;

	const isLoading = loadingStatus === 'pending';

	// Upcoming work orders (due in next 14 days) - COMMENTED OUT
	// const upcomingWorkOrders = useMemo(() => {
	// 	const now = moment();
	// 	const twoWeeksFromNow = moment().add(14, 'days');
	// 	return (workOrdersData?.rows || [])
	// 		.filter((wo) => {
	// 			if (!wo.dueDate) return false;
	// 			const dueDate = moment(wo.dueDate);
	// 			return (
	// 				dueDate.isBetween(now, twoWeeksFromNow) &&
	// 				!['completed', 'cancelled'].includes(wo.status)
	// 			);
	// 		})
	// 		.sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate)))
	// 		.slice(0, 5);
	// }, [workOrdersData]);

	// Mobile card component
	const WorkOrderCard = ({ workOrder }) => {
		// Due date status - COMMENTED OUT
		// const { status: dueDateStatus } = getDueDateStatus(
		// 	workOrder.dueDate,
		// 	workOrder.status,
		// );
		// const isOverdue = dueDateStatus === 'overdue';

		return (
			<div
				className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors bg-white"
				onClick={() => handleRowClick(workOrder)}
			>
				<div className="flex items-start justify-between mb-2">
					<span className="text-xs text-gray-500 font-mono">
						{workOrder.workOrderCode || `WO-${workOrder.id?.slice(-4)}`}
					</span>
					<StatusBadge status={workOrder.status} />
				</div>
				<h3 className="text-sm font-medium text-gray-900 mb-2">
					{workOrder.title || 'Untitled Work Order'}
				</h3>
				<div className="flex flex-wrap gap-2 mb-3">
					<span className="text-xs text-gray-500">
						{workOrder.category?.name || 'Uncategorized'}
					</span>
					{workOrder.assignedTo && (
						<>
							<span className="text-xs text-gray-300">•</span>
							<span className="text-xs text-gray-500">
								{getUserFullName(workOrder.assignedTo)}
							</span>
						</>
					)}
					{workOrder.checklistItems?.length > 0 && (
						<>
							<span className="text-xs text-gray-300">•</span>
							<span
								className={`text-xs ${workOrder.checklistItems.filter((i) => i.completed).length === workOrder.checklistItems.length ? 'text-green-600' : 'text-gray-500'}`}
							>
								{workOrder.checklistItems.filter((i) => i.completed).length}/
								{workOrder.checklistItems.length} tasks
							</span>
						</>
					)}
				</div>
				{/* Due date display - COMMENTED OUT */}
				{/* {workOrder.dueDate && (
					<div
						className={`text-xs mb-3 ${getDueDateStatus(workOrder.dueDate, workOrder.status).className}`}
					>
						Due: {moment(workOrder.dueDate).format('MMM D, YYYY')}
					</div>
				)} */}
				<div className="flex items-center justify-end pt-3 border-t border-gray-100">
					<span className="text-xs text-gray-400">
						Created {moment(workOrder.createdAt).format('MMM D, YYYY')}
					</span>
				</div>
			</div>
		);
	};

	const columns = [
		{
			key: 'workOrderCode',
			header: 'Code',
			width: '120px',
			render: (workOrder) => (
				<span className="text-xs text-gray-500 font-mono">
					{workOrder.workOrderCode || `WO-${workOrder.id?.slice(-4)}`}
				</span>
			),
		},
		{
			key: 'status',
			header: 'Status',
			width: '130px',
			render: (workOrder) => {
				const serviceReportMissing =
					workOrder.serviceReportRequired && !workOrder.serviceReportId;
				const showReportBadge =
					serviceReportMissing &&
					['completed', 'on_hold'].includes(workOrder.status);
				return (
					<StatusBadge
						status={showReportBadge ? 'action_required' : workOrder.status}
					/>
				);
			},
		},

		{
			key: 'title',
			header: 'Title',
			render: (workOrder) => {
				return (
					<div className="flex items-start gap-2">
						<TextStack
							primary={workOrder.title}
							primaryFallback="Untitled Work Order"
							secondary={[
								workOrder.category?.name || 'Uncategorized',
								workOrder.assignedTo
									? getUserFullName(workOrder.assignedTo)
									: 'Unassigned',
								workOrder.contract?.name,
							]}
							capitalize
						/>
					</div>
				);
			},
		},
		{
			key: 'servicePeriod',
			header: 'Service Period',
			width: '180px',
			render: (workOrder) => {
				const start = workOrder.scheduledStartDate;
				const end = workOrder.scheduledEndDate;

				if (!start && !end) {
					return <span className="text-xs text-gray-400">-</span>;
				}

				const startFormatted = start ? moment(start).format('MMM D') : '';
				const endFormatted = end ? moment(end).format('MMM D, YYYY') : '';

				// If same day, show just one date
				if (start && end && moment(start).isSame(moment(end), 'day')) {
					return (
						<span className="text-xs text-gray-600">
							{moment(start).format('MMM D, YYYY')}
						</span>
					);
				}

				// Show range
				if (start && end) {
					return (
						<span className="text-xs text-gray-600">
							{startFormatted} - {endFormatted}
						</span>
					);
				}

				// Only start or end
				return (
					<span className="text-xs text-gray-600">
						{start
							? moment(start).format('MMM D, YYYY')
							: moment(end).format('MMM D, YYYY')}
					</span>
				);
			},
		},
		{
			key: 'tasks',
			header: 'Tasks',
			width: '100px',
			render: (workOrder) => {
				const total = workOrder.checklistItems?.length || 0;
				const completed =
					workOrder.checklistItems?.filter((item) => item.completed).length ||
					0;
				if (total === 0)
					return <span className="text-xs text-gray-400">-</span>;
				return (
					<span
						className={`text-xs ${completed === total ? 'text-green-600' : 'text-gray-600'}`}
					>
						{completed}/{total}
					</span>
				);
			},
		},

		// Due date column - COMMENTED OUT
		// {
		// 	key: 'dueDate',
		// 	header: 'Due Date',
		// 	width: '140px',
		// 	render: (workOrder) => {
		// 		const { className, status } = getDueDateStatus(
		// 			workOrder.dueDate,
		// 			workOrder.status,
		// 		);
		//
		// 		return (
		// 			<span className={`text-xs ${className}`}>
		// 				{workOrder.dueDate
		// 					? moment(workOrder.dueDate).format('MMM D, YYYY')
		// 					: '-'}
		// 			</span>
		// 		);
		// 	},
		// },
	];

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading
				actions={[
					{
						name: 'Create Work Order',
						onClick: () =>
							openCreateWorkOrder({
								onSuccess: () => refreshWorkOrders({ pageSize: null }),
							}),
					},
				]}
				divider={false}
			>
				Work Orders
			</PageHeading>

			{/* Stats */}
			<div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
				<StatCard label="Total" value={total} />
				<StatCard
					label="Pending"
					value={pending}
					valueColor="text-yellow-600"
				/>
				<StatCard
					label="Scheduled"
					value={scheduled}
					valueColor="text-blue-600"
				/>
				<StatCard
					label="In Progress"
					value={inProgress}
					valueColor="text-indigo-600"
				/>
				<StatCard
					label="Completed"
					value={completed}
					valueColor="text-green-600"
				/>
			</div>

			{/* Search & Filters */}
			<div className="bg-white rounded-lg mb-6">
				<div className="flex flex-col lg:flex-row lg:items-center gap-4 p-4">
					{/* Search */}
					<div className="flex-1 relative border border-gray-200 rounded-xl">
						<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search work orders..."
							className="w-full pl-12 pr-4 py-2 text-sm bg-transparent border border-gray-200 lg:border-0 rounded-lg lg:rounded-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
						/>
					</div>

					{/* Divider */}
					<div className="hidden lg:block w-px h-8 bg-gray-200" />

					{/* View Toggle */}
					<div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
						<button
							onClick={() => setViewMode('table')}
							className={`p-2 ${viewMode === 'table' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
							title="Table view"
						>
							<TableCellsIcon className="w-5 h-5" />
						</button>
						<button
							onClick={() => setViewMode('calendar')}
							className={`p-2 ${viewMode === 'calendar' ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
							title="Calendar view"
						>
							<CalendarDaysIcon className="w-5 h-5" />
						</button>
					</div>

					{/* Divider */}
					<div className="hidden lg:block w-px h-8 bg-gray-200" />

					{/* Filters */}
					<div className="flex items-center gap-3 flex-wrap">
						<AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
						<FilterDropdown
							label="Status"
							placeholder="All Status"
							value={statusFilter}
							options={[
								{ value: 'all_active', label: 'All Active' },
								{ value: 'draft', label: 'Draft' },
								{ value: 'pending', label: 'Pending' },
								{ value: 'scheduled', label: 'Scheduled' },
								{ value: 'in_progress', label: 'In Progress' },
								{ value: 'on_hold', label: 'On Hold' },
								{ value: 'completed', label: 'Completed' },
								{ value: 'cancelled', label: 'Cancelled' },
							]}
							onChange={handleStatusFilterChange}
							multiple
							multiSelectLabel="Active"
						/>
						{categories.length > 0 && (
							<FilterDropdown
								label="Category"
								placeholder="All Categories"
								value={categoryFilter}
								options={[
									{ value: 'all', label: 'All Categories' },
									...categories.map((cat) => ({
										value: cat.id,
										label: cat.name,
									})),
								]}
								onChange={setCategoryFilter}
							/>
						)}
					</div>
				</div>
			</div>

			{viewMode === 'table' ? (
				<>
					{/* Mobile/Tablet Cards */}
					<div className="lg:hidden space-y-3">
						{isLoading ? (
							<div className="text-center py-8 text-gray-500">Loading...</div>
						) : processedWorkOrders.length === 0 ? (
							<div className="text-center py-8">
								<p className="text-gray-900 font-medium">
									No work orders found
								</p>
								<p className="text-gray-500 text-sm">
									Try adjusting your search or filter criteria
								</p>
							</div>
						) : (
							<>
								{processedWorkOrders
									.slice(0, mobileItemsToShow)
									.map((workOrder) => (
										<WorkOrderCard key={workOrder.id} workOrder={workOrder} />
									))}
								{/* Load more sentinel */}
								{mobileItemsToShow < processedWorkOrders.length && (
									<div
										ref={loadMoreRef}
										className="text-center py-4 text-gray-400 text-sm"
									>
										Loading more...
									</div>
								)}
							</>
						)}
					</div>

					{/* Desktop Table */}
					<div className="hidden lg:block">
						<DataTable
							data={processedWorkOrders}
							columns={columns}
							keyField="id"
							loading={isLoading}
							onRowClick={handleRowClick}
							containerClassName="border border-gray-200 rounded-lg"
							emptyTitle="No work orders found"
							emptyDescription="Try adjusting your search or filter criteria"
							compact
							pagination
							pageSizeOptions={[10, 25, 50]}
							// Row highlighting for overdue - COMMENTED OUT
							// rowClassName={(row) => {
							// 	const { status } = getDueDateStatus(row.dueDate, row.status);
							// 	return status === 'overdue'
							// 		? 'bg-red-50/50 hover:bg-red-50/75'
							// 		: '';
							// }}
						/>
					</div>
				</>
			) : (
				/* Calendar View */
				<Calendar
					events={calendarData || []}
					currentMonth={calendarMonth}
					onMonthChange={setCalendarMonth}
					onEventClick={handleRowClick}
					loading={calendarLoadingStatus === 'pending'}
				/>
			)}
		</MainContent>
	);
};

export default WorkOrders;
